import React, {useCallback, useLayoutEffect, useMemo, useState} from "react";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {CircularProgress, Paper, TextField} from "@mui/material";
import {Edit} from "@mui/icons-material";
import {Row} from "reactstrap";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../../core/services/api/api";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import classnames from "classnames";
import {ProductTypes, WarrantyAndReturnTypes} from "../../../../../../../../../core/constants/enums";
import Chips, {ChipsIconPosition, ChipsTypes} from "../../../../../../../../components/base/chips/chips";
import SerialBarcodeDialog from "../../../../../../../../components/dialogs/serial-barcode";
import {InvoiceInformationViewProductsDataGridColumnNames} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import DateUtils from "../../../../../../../../../core/services/date-utils";
import {DatePicker} from "@mui/x-date-pickers";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import ProductInformationPopover from "../../../../../../../../components/app-specific/draggable-popover/product-information-popover";
import EnvService from "../../../../../../../../../core/services/env";


const dataGridSaveConfig = {
    name: 'InvoiceInformationViewProductsSectionDataGrid',
    version: 3,
}

const InvoiceInformationViewProductsSection = ({
                                                   order = {},
                                                   setOrder,
                                                   orderLoading,
                                                   customerId,
                                               }) => {
    const [updating, setUpdating] = useState(false);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Part No",
                name: InvoiceInformationViewProductsDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinned: true,
            },
            {
                title: "Warehouse",
                name: InvoiceInformationViewProductsDataGridColumnNames.warehouse,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinned: true,
            },
            {
                title: "Description",
                name: InvoiceInformationViewProductsDataGridColumnNames.description,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 250,
            },
            {
                title: "Discount",
                name: InvoiceInformationViewProductsDataGridColumnNames.discount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Order Quantity",
                name: InvoiceInformationViewProductsDataGridColumnNames.quantity,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 120,
            },
            {
                title: "Shipped Quantity",
                name: InvoiceInformationViewProductsDataGridColumnNames.shipQuantity,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 130,
            },
            {
                title: "Back-order Quantity",
                name: InvoiceInformationViewProductsDataGridColumnNames.backOrderQuantity,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 150,
            },
            {
                title: "Unit Price",
                name: InvoiceInformationViewProductsDataGridColumnNames.unitPrice,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Total Price",
                name: InvoiceInformationViewProductsDataGridColumnNames.totalPrice,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Warranty Expiration",
                name: InvoiceInformationViewProductsDataGridColumnNames.warrantyExpiration,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 160,
            },
            {
                title: "Return Expiration",
                name: InvoiceInformationViewProductsDataGridColumnNames.returnExpiration,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 140,
            },
            {
                title: "Serials / Committed Quantities",
                name: InvoiceInformationViewProductsDataGridColumnNames.serials,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 350,
            },
            ...(EnvService.DEV_showOrderProductComments
                ? [{
                    title: "Comment",
                    name: InvoiceInformationViewProductsDataGridColumnNames.comment,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 220,
                }]
                : []),
        ], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /**
     * Submits a new part support expiration date for the selected item.
     *
     * - if the result of the api is successful then updates the list.
     * @type {(function(*=, *=, *=): Promise<undefined|boolean>)}
     */
    const updateItemPartSupportExpirationDate = useCallback(async (item, newValue, typeId) => {
        setUpdating(true);
        const forApi = {
            partNo: item.partNo,
            invoiceNo: order?.invoiceNo,
            newDate: newValue,
            customerId: customerId,
            reqTypeId: typeId,
        }
        const response = await Api.SubmitNewExpirationDateForSerialNumber(forApi);
        if (!isMounted())
            return;
        setUpdating(false);
        if (response?.resultFlag) {
            setOrder(prevState => ({
                ...prevState,
                items: prevState?.items?.map(e => e.id === item.id
                    ? {
                        ...e,
                        partSupportTypesApplicabilities: e.partSupportTypesApplicabilities?.map(e =>
                            e.typeId === typeId
                                ? {
                                    ...e,
                                    partSupportValidDaysCount: Math.abs(DateUtils.server(newValue).diff(DateUtils.serverNow(), 'day'))
                                }
                                : e
                        )
                    }
                    : e
                ) ?? [],
            }))
        }
        return response?.resultFlag;
    }, [customerId, isMounted, order?.invoiceNo, setOrder])

    /**
     * Updates the comment of an invoice line item.
     * @type {(item: any, newValue: string) => Promise<boolean|undefined>}
     */
    const updateItemComment = useCallback(async (item, newValue) => {
        setUpdating(true);
        const response = await Api.updateInvoiceItemComment({
            customerId,
            invoiceNo: order?.invoiceNo,
            partNo: item.partNo,
            orderPartId: item.id,
            comment: newValue?.trim() ?? '',
        });
        if (!isMounted())
            return;
        setUpdating(false);
        if (response?.resultFlag) {
            if (response.data) {
                setOrder(response.data);
            } else {
                setOrder(prevState => ({
                    ...prevState,
                    items: prevState?.items?.map(e => e.id === item.id
                        ? {...e, comment: newValue?.trim() ?? ''}
                        : e
                    ) ?? [],
                }));
            }
        }
        return response?.resultFlag;
    }, [customerId, isMounted, order?.invoiceNo, setOrder])

    /**
     * Commits the edited value of the provided cell belonging to the [rowKey] and [colName].
     * @type {(function(*=, *=, *=, *=): Promise<undefined|boolean>)}
     */
    const onCellEdited = useCallback(async (prevValue, newValue, rowKey, colName) => {
        const item = order?.items?.find(e => e.id === rowKey)
        switch (colName) {
            case InvoiceInformationViewProductsDataGridColumnNames.warrantyExpiration:
                return updateItemPartSupportExpirationDate(item, newValue, WarrantyAndReturnTypes.warranty);
            case InvoiceInformationViewProductsDataGridColumnNames.returnExpiration:
                return updateItemPartSupportExpirationDate(item, newValue, WarrantyAndReturnTypes.return);
            case InvoiceInformationViewProductsDataGridColumnNames.comment:
                return updateItemComment(item, newValue);
            default:
                return;
        }
    }, [order?.items, updateItemComment, updateItemPartSupportExpirationDate])

    /**
     * Determines the value of warranty and return columns of the data-grid.
     * @type {(function(*=, *=): [string, string|undefined])}
     */
    const getPartSupportExpirationDateFromItem = useCallback((item, typeId) => {
        const partSupport = item?.partSupportTypesApplicabilities?.find(e => e.typeId === typeId);
        if (partSupport?.partSupportValidDaysCount == null)
            return [
                'N/A',
                undefined
            ];
        const date = DateUtils.serverNow().add(partSupport.partSupportValidDaysCount, 'day').endOf('day');
        return [
            date.fromNow(),
            date.format(DateUtils.dateFormat)
        ];
    }, [])

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return order?.items?.map(item => {
            // TODO: change it when the back-end is available.
            const partSupportExpirationEditable = false;
            const [warrantyValue, warrantyEditableValue] = getPartSupportExpirationDateFromItem(item, WarrantyAndReturnTypes.warranty);
            const [returnValue, returnEditableValue] = getPartSupportExpirationDateFromItem(item, WarrantyAndReturnTypes.return);
            const commentEditable = EnvService.DEV_showOrderProductComments && operatorAccessLevels.packages.sales.manageInvoices;
            return ({
                key: item.id,
                data: item,
                cells: {
                    [InvoiceInformationViewProductsDataGridColumnNames.partNo]:
                        <ProductInformationPopover partNo={order?.appliedGiftCardInfo?.equalPartNo === item?.partNo ? null : item?.partNo}>
                            {item.partNo ?? ""}
                        </ProductInformationPopover>,
                    [InvoiceInformationViewProductsDataGridColumnNames.warehouse]: item.whse,
                    [InvoiceInformationViewProductsDataGridColumnNames.description]:
                        <div dangerouslySetInnerHTML={{__html: item?.description ?? ''}}/>,
                    [InvoiceInformationViewProductsDataGridColumnNames.discount]: `${item.discountPct ?? '0'} %`,
                    [InvoiceInformationViewProductsDataGridColumnNames.quantity]: item.orderQty,
                    [InvoiceInformationViewProductsDataGridColumnNames.shipQuantity]: item.committedQty,
                    [InvoiceInformationViewProductsDataGridColumnNames.backOrderQuantity]: item.backorderQty,
                    [InvoiceInformationViewProductsDataGridColumnNames.unitPrice]: item.unitPriceBeforeDiscount ?? 0,
                    [InvoiceInformationViewProductsDataGridColumnNames.totalPrice]: (Number(item.unitPrice ?? '0') * (item.orderQty ?? 0)),
                    [InvoiceInformationViewProductsDataGridColumnNames.warrantyExpiration]: {
                        value: ({openEditPopover}) => <>
                            {
                                partSupportExpirationEditable && operatorAccessLevels.packages.sales.manageInvoices &&
                                <IconButton
                                    color={'secondary'}
                                    onPressed={() => openEditPopover()}
                                    disabled={updating}
                                >
                                    {
                                        updating
                                            ? <CircularProgress size={20} color={'secondary'}/>
                                            : <Edit className={"icon small"}/>
                                    }
                                </IconButton>
                            }
                            <p {...dataGridCellTextProps}
                               className={classnames({'ml-2': partSupportExpirationEditable && operatorAccessLevels.packages.sales.manageInvoices})}>
                                {warrantyValue}
                            </p>
                        </>,
                        editable: partSupportExpirationEditable && operatorAccessLevels.packages.sales.manageInvoices,
                        editableValue: warrantyEditableValue,
                        EditableContentComponent: <ExpirationEditor/>,
                    },
                    [InvoiceInformationViewProductsDataGridColumnNames.returnExpiration]: {
                        value: ({openEditPopover}) => <>
                            {
                                partSupportExpirationEditable && operatorAccessLevels.packages.sales.manageInvoices &&
                                <IconButton
                                    color={'secondary'}
                                    onPressed={() => openEditPopover()}
                                    disabled={updating}
                                >
                                    {
                                        updating
                                            ? <CircularProgress size={20} color={'secondary'}/>
                                            : <Edit className={"icon small"}/>
                                    }
                                </IconButton>
                            }
                            <p {...dataGridCellTextProps}
                               className={classnames({'ml-2': partSupportExpirationEditable && operatorAccessLevels.packages.sales.manageInvoices})}>
                                {returnValue}
                            </p>
                        </>,
                        editable: partSupportExpirationEditable && operatorAccessLevels.packages.sales.manageInvoices,
                        editableValue: returnEditableValue,
                        EditableContentComponent: <ExpirationEditor/>,
                    },
                    [InvoiceInformationViewProductsDataGridColumnNames.serials]:
                        !item?.typeInfo?.code || item?.typeInfo?.code === ProductTypes.nonPhysical
                            ? '--'
                            : item.isSerialized
                                ? <SerialsContainer serials={item?.serials ?? []}/>
                                : '--',
                    ...(EnvService.DEV_showOrderProductComments && {
                        [InvoiceInformationViewProductsDataGridColumnNames.comment]:
                            commentEditable
                                ? {
                                    value: ({openEditPopover}) => <>
                                        <IconButton
                                            color={'secondary'}
                                            onPressed={() => openEditPopover()}
                                            disabled={updating}
                                        >
                                            {
                                                updating
                                                    ? <CircularProgress size={20} color={'secondary'}/>
                                                    : <Edit className={"icon small"}/>
                                            }
                                        </IconButton>
                                        <p {...dataGridCellTextProps} className={'ml-2'}>
                                            {item?.comment?.trim() ? item.comment : '--'}
                                        </p>
                                    </>,
                                    editable: true,
                                    editableValue: item?.comment ?? '',
                                    EditableContentComponent: <CommentEditor/>,
                                }
                                : (item?.comment?.trim() ? item.comment : '--'),
                    }),
                },
            })
        })
    }, [order?.items, order?.appliedGiftCardInfo?.equalPartNo, getPartSupportExpirationDateFromItem, operatorAccessLevels.packages.sales.manageInvoices, updating])

    return (
        <Row className='no-gutters w-100 align-items-start justify-content-center'>
            <Paper className='paper w-100 px-3 py-2'>
                <Row className='no-gutters align-items-center justify-content-between w-100'>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        columns={dataGridColumns}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        hideFooter
                        hideExporting
                        visibleRows={10}
                        rows={dataGridRows}
                        state={{
                            loading: {state: orderLoading},
                            density: dataGridDensity,
                        }}
                        onCellEdited={onCellEdited}
                    />
                    <div className='my-2 w-100'/>
                </Row>
            </Paper>
        </Row>
    )
}


const SerialsContainer = ({serials}) => {
    const [selectedSerial, setSelectedSerial] = useState();

    const chips = useMemo(() => {
        return serials?.map(serialNumber =>
            <span key={`${serialNumber.id}`} {...dataGridCellTextProps} className={'mr-2 mb-2'}>
            <Chips
                type={ChipsTypes.next}
                iconPosition={ChipsIconPosition.end}
                content={serialNumber.serialNumber}
                onIconClick={() => setSelectedSerial(serialNumber)}
            />
            </span>
        )
    }, [serials])

    return (
        <>
            <div className={'d-flex w-100 flex-wrap'}>
                {chips}
            </div>
            <SerialBarcodeDialog
                open={!!selectedSerial}
                setOpen={() => setSelectedSerial(undefined)}
                serial={selectedSerial}
            />
        </>
    );
}


const CommentEditor = ({value, setValue, setCanSubmitChanges}) => {
    useLayoutEffect(() => {
        setCanSubmitChanges(true);
    }, [setCanSubmitChanges]);

    const onValueChanged = useCallback((e) => {
        setValue(e.target.value);
    }, [setValue]);

    return (
        <TextField
            fullWidth
            label={"Comment"}
            size={'small'}
            multiline
            minRows={3}
            value={value?.toString() ?? ''}
            onChange={onValueChanged}
            sx={{
                minWidth: 280,
            }}
        />
    );
}

const ExpirationEditor = ({value: _value, setValue: _setValue, setCanSubmitChanges}) => {
    const [value, setValue] = useState(_value ? DateUtils.serverToUTC(_value) : undefined);

    /**
     * With each change in the value of the editor:
     * - Determines whether the new value is can be submitted.
     */
    useLayoutEffect(() => {
        setValue(_value ? DateUtils.serverToUTC(_value) : undefined);
    }, [_value])


    /**
     * With each change in the value of the editor:
     * - Determines whether the new value is can be submitted.
     */
    useLayoutEffect(() => {
        if (!value)
            return setCanSubmitChanges(false);
        if (!value.isValid())
            return setCanSubmitChanges(false);
        if (value.isBefore(DateUtils.UTCNow().startOf('day')))
            return setCanSubmitChanges(false);
        setCanSubmitChanges(true);
        _setValue(DateUtils.UTCToServer(value).format(DateUtils.withoutOffsetFormat));
    }, [value, _setValue, setCanSubmitChanges])


    return (
        <>
            <DatePicker
                label={"Expiration"}
                onChange={(v) => setValue(v)}
                value={value}
                disablePast
                slotProps={{
                    textField: {
                        fullWidth: true,
                        size: 'small',
                    },
                }}
            />
        </>
    );
}

export default InvoiceInformationViewProductsSection;
